import { defineAsyncComponent } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { AuthService } from '@/services/AuthService'
import { EventBus } from '@/utils/eventBus'

// Layouts (giữ nguyên, không lazy-load)
import MainLayout from '@/layouts/MainLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import StaffLayout from '@/layouts/StaffLayout.vue'

// Not found
import NotFoundView from '@/views/NotFoundView.vue'

// Public (lazy-load các view nặng)
const Home = defineAsyncComponent(() => import('@/views/public/HomeView.vue'))
const About = defineAsyncComponent(() => import('@/views/public/AboutView.vue'))
const Category = defineAsyncComponent(() => import('@/views/public/CategoryView.vue'))
const LoginView = defineAsyncComponent(() => import('@/views/public/LoginView.vue'))
const ForgotPassword = defineAsyncComponent(() => import('@/views/public/ForgotPassword.vue'))
const ChangePassword = defineAsyncComponent(() => import('@/views/public/ChangePassword.vue'))
const RegisterView = defineAsyncComponent(() => import('@/views/public/RegisterView.vue'))

// Auth
const CustomerView = defineAsyncComponent(() => import('@/views/auth/customer/CustomerView.vue'))
const CustomerOrderView = defineAsyncComponent(() => import('@/views/auth/customer/CustomerDetail.vue'))
const ProfileView = defineAsyncComponent(() => import('@/views/auth/profile/ProfileView.vue'))

const StaffView = defineAsyncComponent(() => import('@/views/auth/staff/StaffView.vue'))
const StaffAdd = defineAsyncComponent(() => import('@/views/auth/staff/StaffAdd.vue'))
const StaffUpdate = defineAsyncComponent(() => import('@/views/auth/staff/StaffUpdate.vue'))

const BrandView = defineAsyncComponent(() => import('@/views/auth/brand/BrandView.vue'))
const CategoryView = defineAsyncComponent(() => import('@/views/auth/category/CategoryView.vue'))

const ProductView = defineAsyncComponent(() => import('@/views/auth/product/ProductView.vue'))
const ProductAdd = defineAsyncComponent(() => import('@/views/auth/product/ProductAdd.vue'))
const ProductUpdate = defineAsyncComponent(() => import('@/views/auth/product/ProductUpdate.vue'))
const ProductDetail = defineAsyncComponent(() => import('@/views/auth/product/ProductDetail.vue'))

const OrderView = defineAsyncComponent(() => import('@/views/auth/order/OrderView.vue'))
const OrderSell = defineAsyncComponent(() => import('@/views/auth/order/OrderSell.vue'))
const CheckoutStaff = defineAsyncComponent(() => import('@/views/auth/order/CheckoutStaff.vue'))
const CheckoutCustomer = defineAsyncComponent(() => import('@/views/auth/order/CheckoutCustomer.vue'))
const MyOrder = defineAsyncComponent(() => import('@/views/auth/order/MyOrder.vue'))

const CartView = defineAsyncComponent(() => import('@/views/auth/cart/CartView.vue'))

const PaymentView = defineAsyncComponent(() => import('@/views/auth/payment/PaymentView.vue'))
const PaymentResult = defineAsyncComponent(() => import('@/views/auth/payment/PaymentResult.vue'))

const StatisticView = defineAsyncComponent(() => import('@/views/auth/statistic/StatisticView.vue'))

const InventoryView = defineAsyncComponent(() => import('@/views/auth/inventory/InventoryView.vue'))
const InventoryImport = defineAsyncComponent(() => import('@/views/auth/product/InventoryImport.vue'))

const DiscountView = defineAsyncComponent(() => import('@/views/auth/discount/DiscountView.vue'))
const DiscountOrderRelative = defineAsyncComponent(() => import('@/views/auth/discount/DiscountOrderRelative.vue'))

const BannerView = defineAsyncComponent(() => import('@/views/auth/banner/BannerView.vue'))

const BlogView = defineAsyncComponent(() => import('@/views/auth/blog/BlogView.vue'))
const BlogActive = defineAsyncComponent(() => import('@/views/auth/blog/BlogActive.vue'))
const BlogAdd = defineAsyncComponent(() => import('@/views/auth/blog/BlogAdd.vue'))
const BlogUpdate = defineAsyncComponent(() => import('@/views/auth/blog/BlogUpdate.vue'))
const BlogDetail = defineAsyncComponent(() => import('@/views/auth/blog/BlogDetail.vue'))

const Dispath = defineAsyncComponent(() => import('@/views/auth/dashboard/dispathView.vue'))

// Role metadata
const ROLE_CUSTOMER = { requiresAuth: true, roles: ['ROLE_CUSTOMER'] }
const ROLE_STAFF = { requiresAuth: true, roles: ['ROLE_EMPLOYER', 'ROLE_ADMIN'] }
const ROLE_ADMIN = { requiresAuth: true, roles: ['ROLE_ADMIN'] }

// Routes
const routes = [
    {
        path: '/',
        component: MainLayout,
        children: [
            { path: '', name: 'home', component: Home },
            { path: 'product/detail/:id', name: 'detail', component: ProductDetail },
            { path: 'order/my-order', name: 'myOrder', component: MyOrder, meta: ROLE_CUSTOMER },
            { path: 'payment/vn-pay-callback', name: 'paymentResult', component: PaymentResult },
            { path: 'order/checkout-customer/:id', name: 'checkoutCustomer', component: CheckoutCustomer },
            { path: "category/:categoryId", name: "categoryList", component: Category },
            { path: 'about', name: 'about', component: About },
            { path: 'blog/active', name: 'blogActive', component: BlogActive },
            { path: 'blog/detail/:id', name: 'blogDetail', component: BlogDetail },
            { path: 'profile', name: 'profile', component: ProfileView, meta: ROLE_CUSTOMER },
            { path: 'cart', name: 'myCart', component: CartView, meta: ROLE_CUSTOMER },
        ]
    },
    {
        path: '/',
        component: StaffLayout,
        children: [
            { path: 'dispath', name: 'dispath', component: Dispath, meta: ROLE_STAFF },
            { path: 'profile-staff', name: 'profileStaff', component: ProfileView, meta: ROLE_STAFF },

            { path: 'customer', name: 'customer', component: CustomerView, meta: ROLE_ADMIN },
            { path: 'customer-detail/:id', name: 'customerDetail', component: CustomerOrderView, meta: ROLE_STAFF },

            { path: 'staff', name: 'staff', component: StaffView, meta: ROLE_ADMIN },
            { path: 'staff/add', name: 'staffAdd', component: StaffAdd, meta: ROLE_ADMIN },
            { path: 'staff/update/:id', name: 'staffUpdate', component: StaffUpdate, meta: ROLE_ADMIN },

            { path: 'brand', name: 'brand', component: BrandView, meta: ROLE_ADMIN },
            { path: 'category', name: 'category', component: CategoryView, meta: ROLE_ADMIN },

            { path: 'product', name: 'product', component: ProductView, meta: ROLE_ADMIN },
            { path: 'product/add', name: 'productAdd', component: ProductAdd, meta: ROLE_ADMIN },
            { path: 'product/update/:id', name: 'productUpdate', component: ProductUpdate, meta: ROLE_ADMIN },

            { path: 'order', name: 'order', component: OrderView, meta: ROLE_STAFF },
            { path: 'order/sell', name: 'orderSell', component: OrderSell, meta: ROLE_STAFF },
            { path: 'order/checkout-staff/:id', name: 'checkoutStaff', component: CheckoutStaff, meta: ROLE_STAFF },
            { path: 'order/discount/:id', name: 'orderUsedDiscount', component: DiscountOrderRelative, meta: ROLE_STAFF },

            { path: 'payment', name: 'payment', component: PaymentView, meta: ROLE_STAFF },

            { path: 'statistics', name: 'statistics', component: StatisticView, meta: ROLE_ADMIN },

            { path: 'inventory', name: 'inventory', component: InventoryView, meta: ROLE_STAFF },
            { path: 'inventory/import', name: 'inventoryImport', component: InventoryImport, meta: ROLE_STAFF },

            { path: 'discount', name: 'discount', component: DiscountView, meta: ROLE_ADMIN },

            { path: 'banner', name: 'banner', component: BannerView, meta: ROLE_ADMIN },

            { path: 'blog', name: 'blog', component: BlogView, meta: ROLE_ADMIN },
            { path: 'blog/add', name: 'blogAdd', component: BlogAdd, meta: ROLE_ADMIN },
            { path: 'blog/update/:id', name: 'blogUpdate', component: BlogUpdate, meta: ROLE_ADMIN },
        ]
    },
    {
        path: '/',
        component: AuthLayout,
        children: [
            { path: 'login', name: 'login', component: LoginView },
            { path: 'forgot-password', name: 'forgotPassword', component: ForgotPassword },
            { path: 'change-password', name: 'changePassword', component: ChangePassword },
            { path: 'register', name: 'register', component: RegisterView }
        ]
    },
    {
        path: '/:pathMatch(.*)*', name: 'notFound', component: NotFoundView
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes
})

// Global route guard
router.beforeEach(async (to, from, next) => {
    if (!to.meta.requiresAuth) {
        next()
        return
    }

    let isAuth = AuthService.isAuthenticated()
    if (!isAuth) {
        const ok = await AuthService.bootstrapAuth()
        if (!ok) {
            next({ name: 'login', query: { urlReturn: to.fullPath } })
            return
        }
    }

    const role = AuthService.getRole()
    if (to.meta.roles && !to.meta.roles.includes(role)) {
        EventBus.showNotify('Bạn không có quyền truy cập', 'error')
        next(false)
        return
    }

    next()
})

export default router
